import { useEffect, useMemo, useState, useRef } from "react";

import { ensureCsrfToken, getLegacyUrl } from "../../api";

import InvoiceCheckDetailModal from "../../components/InvoiceCheckDetailModal";

import ProcessorViewShell from "./ProcessorViewShell";

import { openInvoiceData } from "../../utils/invoiceDisplay";
import { openInvoicePdf } from "../../utils/openPdf";

import Swal from "sweetalert2";


function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function isAccountBlocked(row) {
  const isBlocked = (value) => {
    const indicator = String(value || "").trim();
    return indicator !== "" && indicator !== "N";
  };

  // return isBlocked(row.account_indicator) || isBlocked(row.payment_indicator);
  return isBlocked(row.account_indicator);
}

function buildGenerateSuccessMessage(payload) {
  const blocked = payload?.blocked_entries || [];
  const skipped = payload?.skipped_entries || [];

  if (!blocked.length && !skipped.length) {
    return "Templates generated successfully.";
  }

  let message = "";

  if (blocked.length) {
    message +=
      "Posting Date belongs to period which is closed as per configuration.\n\n" +
      `Blocked Invoice IDs: ${blocked.join(", ")}\n\n`;
  }

  if (skipped.length) {
    const skippedList = skipped
      .map((item) => `Invoice ${item.InvoiceId} -> ${item.reason}`)
      .join("\n");

    message +=
      "Some invoices were skipped due to missing mandatory mapped fields.\n\n" +
      `${skippedList}\n\n`;
  }

  message += "Templates generated successfully for remaining entries.";
  return message;
}

function StatusModal({ detail, onClose }) {
  if (!detail) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ready-status-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Status Detail</p>
            <h2 id="ready-status-title">{detail.label}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-note">{detail.message || "No additional message available."}</p>
        <pre className="processor-modal__code">
          {JSON.stringify(detail.data ?? {}, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function TrailModal({ trail, loading, onClose }) {

  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}

export default function ProcessorReadyView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [statusDetail, setStatusDetail] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const filteredRows = useMemo(() => {
    if (filter === "account_block_only") {
      return rows.filter((row) => isAccountBlocked(row));
    }

    if (filter === "all_minus_account_block") {
      return rows.filter((row) => !isAccountBlocked(row));
    }

    return rows;
  }, [filter, rows]);

  const allVisibleSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedRows.includes(row.InvoiceGroupKey));

  const summary = useMemo(
    () => ({
      count: rows.length,
      visible: filteredRows.length,
      blocked: rows.filter((row) => isAccountBlocked(row)).length,
      selected: selectedRows.length,
    }),
    [filteredRows, rows, selectedRows],
  );

  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuPosition) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuPosition]);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-ready/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Unable to load ready invoices. Backend responded with ${response.status}.`,
        );
      }

      if (!payload) {
        throw new Error("Unable to load ready invoices. Backend returned a non-JSON response.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load ready invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  function toggleRowSelection(invoiceGroupKey) {
    setSelectedRows((current) =>
      current.includes(invoiceGroupKey)
        ? current.filter((item) => item !== invoiceGroupKey)
        : [...current, invoiceGroupKey],
    );
  }

  function toggleAllVisibleRows() {
    const visibleKeys = filteredRows.map((row) => row.InvoiceGroupKey);

    setSelectedRows((current) => {
      if (allVisibleSelected) {
        return current.filter((item) => !visibleKeys.includes(item));
      }

      return Array.from(new Set([...current, ...visibleKeys]));
    });
  }

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.InvoiceId}/`, {
        credentials: "include",
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Unable to load trail.");
      }

      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  

  async function handlePreview(row) {
    setStatus("");
    setError("");
    setActionKey(`preview-${row.invoiceGroupKey}`);

    // Open immediately from user click
    const previewWindow = window.open("", "_blank");

    if (!previewWindow) {
      setError("Preview window was blocked by the browser.");
      return;
    }

    previewWindow.document.write(
      "<html><body><h3>Loading preview...</h3></body></html>"
    );

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/template-preview/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_keys: [row.InvoiceGroupKey],
          material_service: row.material_service,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload?.status !== "success") {
        throw new Error(payload?.message || "Unable to generate preview.");
      }

      let html =
        "<html><head><title>Preview</title></head><body style='padding:24px;font-family:Segoe UI,sans-serif;'>";

      Object.entries(payload.template_html || {}).forEach(([key, value]) => {
        html += `<h3>${key}</h3>${value}<hr/>`;
      });

      html += "</body></html>";

      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();

    } catch (requestError) {
      previewWindow.close();
      setError(requestError.message || "Unable to generate preview.");
    } finally {
      setActionKey("");
    }
  }

  async function handleGenerateTemplates() {
    setStatus("");
    setError("");

    const selectedItems = rows.filter((row) => selectedRows.includes(row.InvoiceGroupKey));
    const selectedKeys = selectedItems
      .filter((row) => !isAccountBlocked(row))
      .map((row) => row.InvoiceGroupKey);
    const blockedKeys = selectedItems
      .filter((row) => isAccountBlocked(row))
      .map((row) => row.InvoiceGroupKey);

    if (!selectedKeys.length && !blockedKeys.length) {
      setError("Please select at least one invoice to generate template.");
      return;
    }

    let finalKeysToSend = [...selectedKeys];

    if (blockedKeys.length) {
      const confirmProcess = window.confirm(
        "Some selected invoices have Account Block applied.\n\nOK -> Process all invoices\nCancel -> Skip blocked invoices",
      );

      if (confirmProcess) {
        const finalConfirm = window.confirm(
          "Are you sure you want to generate the template for\nselected Account Block invoices?",
        );

        if (!finalConfirm) {
          return;
        }

        finalKeysToSend = selectedKeys.concat(blockedKeys);
      }
    }

    if (!finalKeysToSend.length) {
      setError("No invoices available to process after filtering.");
      return;
    }

    setActionKey("generate");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/template-generate/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ invoice_keys: finalKeysToSend }),
      });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (payload?.skipped_invoices?.length || payload?.blocked_entries?.length) {
        let html = "";

        if (payload.skipped_invoices?.length) {
          html += "<b>Skipped Invoices</b><br><ul>";

          payload.skipped_invoices.forEach((item) => {
            html += `<li><b>${item.InvoiceId}</b> - ${item.reason}</li>`;
          });

          html += "</ul>";
        }

        if (payload.blocked_entries?.length) {
          html += "<br><b>Blocked Invoices</b><br><ul>";

          payload.blocked_entries.forEach((item) => {
            html += `<li><b>${item.InvoiceId}</b> - ${item.reason}</li>`;
          });

          html += "</ul>";
        }

        await Swal.fire({
          icon: "warning",
          title: "Some invoices could not be generated",
          html,
          width: 700,
        });
      }

      setStatus(buildGenerateSuccessMessage(payload));
      setSelectedRows([]);
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || "Error sending data to backend.");
    } finally {
      setActionKey("");
    }
  }

  async function handleAction(row, type) {
    setStatus("");
    setError("");

    if (
      type === "approve" &&
      Object.values(row.invoiceCheck || {}).some((item) => item?.color === "r")
    ) {
      setError("There are discrepancies in this invoice. Fix them before approving.");
      return;
    }

    setActionKey(`${type}-${row.invoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "hold") {
        url = "/api/hold-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "delete") {
        url = "/api/delete-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab: "pendingtab" };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}d successfully.`);
      setSelectedRows((current) => current.filter((item) => item !== row.invoiceGroupKey));
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  function refreshPageItems() {
    loadRows();
  }

  return (
    <ProcessorViewShell
      title="Ready To Generate Template"
      intro=""
      metrics={[
        { label: "Queue", value: summary.count },
        
      ]}
      actions={
        <button
          className="ghost-button processor-view__refresh"
          type="button"
          onClick={refreshPageItems}
        >
          Refresh
        </button>
      }
    >
      <section className="processor-view">
        <div className="workspace-panel">
          <div className="processor-table__header">

            <div className="config-inline">
              <div className="config-field">
                <label htmlFor="ready-filter">Invoice Filter</label>
                <select id="ready-filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
                  <option value="all">All</option>
                  <option value="account_block_only">Account Block Only</option>
                  <option value="all_minus_account_block">Other Then Account Block</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <p className="config-note">Loading ready invoices…</p>
          ) : (
            <>
              <div className="config-table processor-table">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice ID</th>
                      <th>Vendor Name</th>
                      <th>Invoice No.</th>
                      <th>Invoice Date</th>
                      <th>Invoice Value</th>
                      <th>Invoice Status</th>
                      <th>Response</th>
                      <th>File</th>
                      <th>Preview</th>
                      <th>Logs</th>
                      <th>
                        Select
                        <input
                          className="processor-checkbox"
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisibleRows}
                        />
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length ? (
                      filteredRows.map((row) => (
                        <tr key={row.InvoiceGroupKey}>
                          <td>{row.InvoiceId}</td>
                          <td>{row.VendorName || "-"}</td>
                          <td>{row.InvoiceNo || row.InvNo || "-"}</td>
                          <td>{row.InvoiceDate || row.InvDate || "-"}</td>
                          <td>{formatAmount(row.InvoiceValue || row.TotalAmount)}</td>
                          <td>
                            <div className="processor-status-list">
                              {Object.entries(row.InvoiceGroupKey || {}).length ? (
                                Object.entries(row.InvoiceCheck || {}).map(([label, item]) => (
                                  <button
                                    key={`${row.InvoiceGroupKey}-${label}`}
                                    className={`processor-status-dot processor-status-dot--${item?.color || "o"}`}
                                    type="button"
                                    title={`${label}: ${item?.message || ""}`}
                                    onClick={() =>
                                      setStatusDetail({
                                        label,
                                        message: item?.message,
                                        data: item?.data,
                                        key: item?.key || label,
                                        checkKey: label,
                                        uniqueKey: row.InvoiceGroupKey,
                                        color: item?.color || "o",
                                      })
                                    }
                                  />
                                ))
                              ) : (
                                <span className="empty-state">No checks</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {row.unique_name ? (
                              <button
                                onClick={() =>
                                  openInvoiceData(
                                    `${row.unique_name}.json`
                                  )
                                }
                              >
                                Data
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                              {row.InvoiceId ? (
                                <button
                                  type="button"
                                  onClick={() => openInvoicePdf(row.InvoiceId)}
                                >
                                  📎
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          <td>
                            <button
                              className="ghost-button"
                              type="button"
                              disabled={actionKey === `preview-${row.InvoiceGroupKey}`}
                              onClick={() => handlePreview(row)}
                            >
                              {actionKey === `preview-${row.InvoiceGroupKey}` ? "Previewing..." : "Preview"}
                            </button>
                          </td>
                          <td>
                            <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                              Logs
                            </button>
                          </td>
                          <td>
                            <input
                              className="processor-checkbox"
                              type="checkbox"
                              checked={selectedRows.includes(row.InvoiceGroupKey)}
                              onChange={() => toggleRowSelection(row.InvoiceGroupKey)}
                            />
                          </td>
                          <td>
                            <div className="action-dropdown-wrapper">
                              <button
                                type="button"
                                className="action-menu-button"
                                onClick={(e) => {

                                  if (
                                    menuPosition?.row?.InvoiceGroupKey === row.InvoiceGroupKey
                                  ) {
                                    setMenuPosition(null);
                                    return;
                                  }

                                  const rect = e.currentTarget.getBoundingClientRect();

                                  setMenuPosition({
                                    row,
                                    top: rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX,
                                  });
                                }}
                              >
                                Actions ▼
                              </button>                         
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11">No invoices found in the ready queue for the selected filter.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <p className="config-note" style={{ margin: 0 }}>
                  {summary.selected} invoice{summary.selected === 1 ? "" : "s"} selected for template generation.
                </p>

                <button
                  className="primary-button"
                  type="button"
                  disabled={actionKey === "generate"}
                  onClick={handleGenerateTemplates}
                >
                  {actionKey === "generate" ? "Generating..." : "Generate Template"}
                </button>
              </div>
            </>
          )}
        </div>

        {menuPosition && (
          <div
            ref={menuRef}
            className="global-action-menu"
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 999999,
            }}
          >
            
            <button
              type="button"
              onClick={() => {
                handleAction(menuPosition.row, "hold");
                setMenuPosition(null);
              }}
            >
              ⏸ Hold
            </button>
          </div>
        )}

        <InvoiceCheckDetailModal
          detail={statusDetail}
          onClose={() => setStatusDetail(null)}
          onSaved={loadRows}
        />
        <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
      </section>
    </ProcessorViewShell>
  );
}
